import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
} from '@mui/material';
import { DocumentUpload } from '../DocumentUpload';

interface DetailedDriverLicenseStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedDriverLicenseStep: React.FC<DetailedDriverLicenseStepProps> = ({
  onNext,
  onBack: _onBack,
  onClose,
  existingData,
  driver: _driver,
  isFirstStep: _isFirstStep,
  isLastStep: _isLastStep,
}) => {
  const [formData, setFormData] = useState({
    licenseTypeId: '',
    issuingJurisdiction: '',
    number: '',
    issuedDate: '',
    expiryDate: '',
    hasDocument: '',
    documentFile: null,
  });

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData }));
    }
  }, [existingData]);

  const provinces = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'YT', label: 'Yukon' },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Prepare data for saving - match backend DTO structure
    const saveData = {
      licenseTypeId: 1, // Default to first license type, should be from API
      code: formData.number, // License number
      expiry: formData.expiryDate, // Expiry date
      status: 'active', // Default status
    };

    onNext(saveData);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        Add Licence
      </Typography>

      <Grid container spacing={3}>
        {/* License Origin */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Licence Origin
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select province...</InputLabel>
            <Select
              value={formData.issuingJurisdiction}
              label="Select province..."
              onChange={e => handleInputChange('issuingJurisdiction', e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              {provinces.map(province => (
                <MenuItem key={province.value} value={province.value}>
                  {province.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Expiry Date */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Expiry Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.expiryDate}
            onChange={e => handleInputChange('expiryDate', e.target.value)}
            sx={{ backgroundColor: 'white' }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* License Number */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            License Number
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter license number"
            value={formData.number}
            onChange={e => handleInputChange('number', e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>

        {/* File Upload Question */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Do you want to upload a scan or image of your licence?
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select answer...</InputLabel>
            <Select
              value={formData.hasDocument}
              label="Select answer..."
              onChange={e => handleInputChange('hasDocument', e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Document Upload */}
        {formData.hasDocument === 'yes' && (
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload License Document
            </Typography>
            <DocumentUpload
              documentType="license"
              acceptTypes="image/*,.pdf"
              maxSizeMB={10}
              driverId={driver?.id}
              onUploadSuccess={fileData => {
                handleInputChange('documentFile', fileData);
              }}
              onUploadError={error => {
                console.error('Upload error:', error);
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 6,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderColor: '#1976d2',
            color: '#1976d2',
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!formData.issuingJurisdiction || !formData.expiryDate || !formData.number}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
            px: 4,
            py: 1.5,
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
